import React, { useEffect, useState } from 'react';
import './TableList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faTrash, faPen, faUsers } from '@fortawesome/free-solid-svg-icons';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://www.fro.ro/api/admins/admin-clubs.php';
const CREATE_URL = 'https://www.fro.ro/api/admins/create-club.php';
const UPDATE_URL = 'https://www.fro.ro/api/admins/update-club.php';
const token = localStorage.getItem('adminToken');

export default function AdminClubManager() {
  const [clubs, setClubs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-az');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newClub, setNewClub] = useState({
    name: '',
    email: '',
    contact_name: '',
    contact_phone: ''
  });
  const [editClub, setEditClub] = useState(null);

  const navigate = useNavigate();

  // === FETCH HELPERS ===
  const fetchCreditData = async (clubId, year) => {
    const res = await fetch(`${API_URL}?action=credit&club_id=${clubId}&year=${year}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    return {
      totalPaid: data.total_paid || 0,
      totalExpenses: data.total_spent || 0,
      credit: data.credit || 0
    };
  };

  const fetchClubs = async () => {
    const res = await fetch(API_URL, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    const year = new Date().getFullYear();

    const clubsWithTotals = await Promise.all(
      data.map(async (club) => {
        const creditData = await fetchCreditData(club.id, year);
        const isActive = creditData.totalPaid > 0;
        return {
          ...club,
          newYear: year,
          newFee: '',
          ...creditData,
          is_active: isActive
        };
      })
    );

    setClubs(clubsWithTotals);
  };

  useEffect(() => { fetchClubs(); }, []);

  const updateYearData = async (clubId, year) => {
    const data = await fetchCreditData(clubId, year);
    const isActive = data.totalPaid > 0;
    return { ...data, is_active: isActive };
  };

  const handleFeeSave = async (club) => {
    if (!club.newYear || !club.newFee) {
      alert("Introdu anul și suma taxei!");
      return;
    }

    try {
      const res = await fetch(`${API_URL}?action=fee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          club_id: club.id,
          year: parseInt(club.newYear),
          amount: parseFloat(club.newFee),
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert(`Taxă salvată pentru anul ${club.newYear}!`);
        fetchClubs();
      } else {
        alert("Eroare: " + (data.message || 'nu s-a putut salva'));
      }
    } catch (err) {
      alert("Eroare la conexiune: " + err.message);
    }
  };

  const handleCreateClub = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(CREATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newClub)
      });
      const data = await res.json();
      if (data.success) {
        alert('Club creat cu succes!');
        setShowAddModal(false);
        setNewClub({ name: '', email: '', contact_name: '', contact_phone: '' });
        fetchClubs();
      } else {
        alert(data.message || 'Eroare la creare club');
      }
    } catch (err) {
      alert('Eroare la conexiune: ' + err.message);
    }
  };

  const handleEditClub = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(UPDATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          id: editClub.id,
          name: editClub.name,
          contact_name: editClub.contact_name,
          contact_phone: editClub.contact_phone,
        }),
      });

      const data = await res.json();
      if (data.success) {
        alert("Club actualizat cu succes!");
        setShowEditModal(false);
        setEditClub(null);
        fetchClubs();
      } else {
        alert("Eroare la actualizare: " + data.message);
      }
    } catch (err) {
      alert("Eroare la conexiune: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Atenție! Această acțiune va șterge definitiv clubul. Ești sigur?")) return;
    try {
      const res = await fetch("https://www.fro.ro/api/admins/delete-club.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      alert(data.message);
      fetchClubs();
    } catch (err) {
      alert("Eroare la conexiune: " + err.message);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Sigur vrei să dezactivezi acest club?")) return;
    try {
      const res = await fetch("https://www.fro.ro/api/admins/deactivate-club.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      alert(data.message);
      fetchClubs();
    } catch {
      alert("Eroare la conexiune");
    }
  };

  // === FILTRARE & SORTARE ===
  const filteredClubs = clubs
    // 🔥 EXCLUDEREA CLUBURILOR SPECIALE
    .filter(c =>
      c.email !== 'admin@fro.ro' &&
      c.email !== 'cch@fro.ro'
    )
    // 🔍 FILTRARE DUPĂ SEARCH
    .filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );


  const sortedClubs = [...filteredClubs].sort((a, b) => {
    if (sortBy === 'name-az') return a.name.localeCompare(b.name);
    if (sortBy === 'budget') return (b.totalPaid || 0) - (a.totalPaid || 0);
    return 0;
  });

  return (
    <div className="table-list-container">
      <div className="table-list-header">
        <h2>Cluburi sportive</h2>
        <div className="sort-bar">
          <label htmlFor="sort-select">Sortează:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="name-az">A-Z după nume</option>
            <option value="budget">Taxe plătite</option>
          </select>
        </div>
        <input
          type="text"
          className="search-bar"
          placeholder="Caută club..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <button
          className="add-circle-btn"
          onClick={() => setShowAddModal(true)}
          aria-label="Adaugă club"
        >
          <span className="plus-icon">+</span>
        </button>
      </div>

      <div className="table-list-wrapper">
        <table className="table-list">

          <thead>
            <tr>
              <th>Status</th>
              <th>Nume club</th>
              <th>Email</th>
              <th>Contact</th>
              <th>Telefon</th>
              <th>Taxe plătite</th>
              <th>Cheltuit</th>
              <th>Rămas</th>
              <th>An</th>
              <th>Taxă nouă</th>
              <th>Salvează</th>
              <th>Membri</th>
              <th>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {sortedClubs.length === 0 ? (
              <tr><td colSpan={13}>Nu există cluburi.</td></tr>
            ) : (
              sortedClubs.map(club => (
                <tr key={club.id}>
                  <td>
                    <span className={`status-badge ${club.is_active ? 'active' : 'inactive'}`}>
                      <span className="status-dot"></span>
                      {club.is_active ? 'Activ' : 'Inactiv'}
                    </span>
                  </td>
                  <td>{club.name}</td>
                  <td>{club.email}</td>
                  <td>{club.contact_name}</td>
                  <td>{club.contact_phone}</td>
                  <td><strong>{club.totalPaid.toFixed(2)} lei</strong></td>
                  <td><strong>{club.totalExpenses.toFixed(2)} lei</strong></td>
                  <td>
                    <strong style={{ color: club.credit <= 0 ? 'red' : 'green' }}>
                      {club.credit.toFixed(2)} lei
                    </strong>
                  </td>

                  <td>
                    <select
                      value={club.newYear || new Date().getFullYear()}
                      onChange={async (e) => {
                        const newYear = e.target.value;
                        const yearData = await updateYearData(club.id, newYear);
                        const updated = clubs.map(c =>
                          c.id === club.id
                            ? { ...c, newYear, ...yearData, is_active: yearData.is_active }
                            : c
                        );
                        setClubs(updated);
                      }}
                      style={{
                        width: 100,
                        padding: '4px',
                        borderRadius: '6px'
                      }}
                    >
                      <option value="2025">2025</option>
                      <option value="2026">2026</option>
                    </select>
                  </td>

                  <td>
                    <input
                      type="number"
                      placeholder="Taxă (lei)"
                      value={club.newFee || ''}
                      onChange={e => {
                        const updated = clubs.map(c =>
                          c.id === club.id ? { ...c, newFee: e.target.value } : c
                        );
                        setClubs(updated);
                      }}
                      style={{ width: 100 }}
                    />
                  </td>
                  <td>
                    <button onClick={() => handleFeeSave(club)}>Salvează</button>
                  </td>

                  {/* 🔹 NOUL BUTON — vezi membrii clubului */}
                  <td>
                    <div className="club-actions-group">
                      <button
                        className="action-btn events"
                        onClick={() => navigate(`/admin/clubs/${club.id}/events`)}
                        title="Vezi evenimentele clubului"
                        style={{ marginLeft: '6px', padding: '6px 55px' }}
                      >
                        <FontAwesomeIcon icon={faCalendarDays} /> Evenimente
                      </button>

                      <button
                        className="action-btn members"
                        onClick={() => navigate(`/admin/clubs/${club.id}/athletes`)}
                        title="Vezi sportivii clubului"
                      >
                        <FontAwesomeIcon icon={faUsers} /> Sportivi
                      </button>

                      <button
                        className="action-btn members"
                        onClick={() => navigate(`/admin/clubs/${club.id}/referees`)}
                        title="Vezi arbitrii clubului"
                      >
                        <FontAwesomeIcon icon={faUsers} /> Arbitri
                      </button>

                      <button
                        className="action-btn coaches"
                        onClick={() => navigate(`/admin/clubs/${club.id}/coaches`)}
                        title="Vezi antrenorii clubului"
                        style={{ marginLeft: '6px', padding: '6px 46px' }}
                      >
                        <FontAwesomeIcon icon={faUsers} /> Antrenori
                      </button>
                    </div>
                  </td>


                  <td className="table-list-actions">
                    <button className="action-btn edit" onClick={() => { setEditClub(club); setShowEditModal(true); }}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button className="action-btn deactivate" onClick={() => handleDeactivate(club.id)}>
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(club.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* === MODALE === */}
        {showAddModal && (
          <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Adaugă club nou</h3>
              <form onSubmit={handleCreateClub} className="modal-form">
                <label>Nume club</label>
                <input type="text" value={newClub.name}
                  onChange={(e) => setNewClub({ ...newClub, name: e.target.value })} required />

                <label>Email</label>
                <input type="email" value={newClub.email}
                  onChange={(e) => setNewClub({ ...newClub, email: e.target.value })} required />

                <label>Persoană contact</label>
                <input type="text" value={newClub.contact_name}
                  onChange={(e) => setNewClub({ ...newClub, contact_name: e.target.value })} />

                <label>Telefon contact</label>
                <input type="text" value={newClub.contact_phone}
                  onChange={(e) => setNewClub({ ...newClub, contact_phone: e.target.value })} />

                <div className="modal-actions">
                  <button type="submit" className="save-btn">Salvează</button>
                  <button type="button" className="cancel-btn" onClick={() => setShowAddModal(false)}>Anulează</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && editClub && (
          <div className="modal-backdrop" onClick={() => setShowEditModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Editează club</h3>
              <form onSubmit={handleEditClub} className="modal-form">
                <label>Nume club</label>
                <input type="text" value={editClub.name}
                  onChange={(e) => setEditClub({ ...editClub, name: e.target.value })} required />
                <label>Persoană contact</label>
                <input type="text" value={editClub.contact_name}
                  onChange={(e) => setEditClub({ ...editClub, contact_name: e.target.value })} />
                <label>Telefon contact</label>
                <input type="text" value={editClub.contact_phone}
                  onChange={(e) => setEditClub({ ...editClub, contact_phone: e.target.value })} />
                <div className="modal-actions">
                  <button type="submit" className="save-btn">Actualizează</button>
                  <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>Anulează</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
